import type { TaskDetail } from '@cis/api-client';
import type { TaskWrite } from '../../api/tasks.ts';
import { Modal } from '../../ui/modal.tsx';
import { writeLabel } from './action-labels.ts';
import { formatDate, STATUS_LABELS } from './format.ts';
import type { TaskWriter } from './use-task-writer.ts';

const names = (d: TaskDetail): string =>
  d.assignees.map((p) => p.displayName).join(', ') || 'nobody';

function tickText(d: TaskDetail, no: number): string {
  const tick = d.steps.find((s) => s.no === no)?.tick ?? null;
  return tick === null ? 'not ticked' : `ticked by ${tick.by.displayName}`;
}

function stepPeople(d: TaskDetail, no: number): string {
  const step = d.steps.find((s) => s.no === no);
  return step?.assignees.map((p) => p.displayName).join(', ') || 'nobody';
}

const textOf = (v: string | null): string => (v === null || v === '' ? 'none' : v);

// Plain-text facts of the task that a person can change, label first (spec §5.1 state fields).
const FACTS: readonly (readonly [string, (d: TaskDetail) => string])[] = [
  ['Status', (d) => STATUS_LABELS[d.state.status]],
  ['Assigned to', names],
  ['Done', (d) => `${String(d.state.percent)}%`],
  ['Due', (d) => formatDate(d.due)],
  ['Starts', (d) => formatDate(d.state.replannedStart ?? d.plannedStart)],
  ['Actual finish', (d) => formatDate(d.state.actualFinish)],
  ['Update notes', (d) => textOf(d.state.updateNotes)],
];

/** What someone else changed between the page the person acted on and the page now. */
export function changesBetween(seen: TaskDetail, now: TaskDetail): string[] {
  const out = FACTS.flatMap(([label, read]) =>
    read(seen) === read(now) ? [] : [`${label}: ${read(seen)} → ${read(now)}`],
  );
  now.steps.forEach((s) => {
    if (tickText(seen, s.no) !== tickText(now, s.no)) {
      out.push(`Step ${String(s.no)}: ${tickText(seen, s.no)} → ${tickText(now, s.no)}`);
    }
    if (stepPeople(seen, s.no) !== stepPeople(now, s.no)) {
      const who = `${stepPeople(seen, s.no)} → ${stepPeople(now, s.no)}`;
      out.push(`Step ${String(s.no)} assigned to: ${who}`);
    }
  });
  return out;
}

function tickStillPossible(write: Extract<TaskWrite, { kind: 'tick' }>, now: TaskDetail): boolean {
  const step = now.steps.find((s) => s.no === write.stepNo);
  return step !== undefined && step.canTick && (step.tick !== null) !== write.ticked;
}

/** Whether the change still makes sense on the page as it is now. */
export function stillPossible(write: TaskWrite, now: TaskDetail): boolean {
  const p = now.permissions;
  switch (write.kind) {
    case 'action':
      return p.actions.includes(write.action);
    case 'assign':
      return p.assign;
    case 'assign-step':
      return p.assign && now.steps.some((s) => s.no === write.stepNo);
    case 'replan':
      return p.replan;
    case 'edit':
      return p.edit && (write.edit.percentOverride === undefined || p.overridePercent);
    case 'tick':
      return tickStillPossible(write, now);
  }
}

/** qa Q21: the 409 "changed by someone else" dialog, with the person's change ready to reapply. */
export function ConflictDialog(props: {
  readonly writer: TaskWriter;
  readonly current: TaskDetail;
  /** True while the newest version of the task is being read after the 409. */
  readonly refreshing: boolean;
}): React.JSX.Element | null {
  const { writer, current, refreshing } = props;
  const conflict = writer.conflict;
  if (conflict === null) return null;
  const changes = changesBetween(conflict.seen, current);
  const possible = !refreshing && stillPossible(conflict.write, current);
  return (
    <Modal title="Someone else changed this task" onClose={writer.dismiss} role="alertdialog">
      <p className="modal__body">
        While you were looking at it, someone else saved a change. Your change (
        {writeLabel(conflict.write)}) was not saved. Here is what is different now:
      </p>
      {refreshing ? (
        <p className="muted">Reading the latest version…</p>
      ) : (
        <ul className="plain-list" aria-label="What changed">
          {changes.length === 0 ? <li>Other details of the task changed.</li> : null}
          {changes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
      {!refreshing && !possible && (
        <p className="muted">Your change no longer applies to the task as it is now.</p>
      )}
      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={writer.dismiss}>
          Keep their version
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!possible || writer.pending}
          onClick={() => void writer.reapply(current)}
        >
          Reapply my change
        </button>
      </div>
    </Modal>
  );
}
